import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Dropdown } from "components/common/Dropdown";
import { useAppDispatch } from "hooks/redux";
import { Product } from "interfaces/products";
import {
  bulkImportActions,
  InvalidEntity,
} from "store/slices/bulkImport";
import { BulkImportEntityType } from 'interfaces/bulkImport';
import { DropdownRendererWrapper } from '../styled';
import { formStateActions } from 'store/slices/formState';
import { ICellEditorParams } from '@ag-grid-community/core';
import { useClickOutside } from 'hooks/useClickOutside';

interface Props extends ICellEditorParams {
  data: InvalidEntity;
  value: InvalidEntity;
  tab: BulkImportEntityType;
  disabled?: boolean;
  options?: any[],
  maxHeight?: number;
  setSelectedPane: (pane: any) => void;
  width: number;
}

export const DropdownRenderer = forwardRef(({
  data,
  value,
  tab,
  disabled,
  options,
  maxHeight,
  setSelectedPane,
  width,
  stopEditing,
}: Props, ref) => {
  const dispatch = useAppDispatch();
  const wrapperRef = React.createRef<HTMLDivElement>();
  const [selectedValue, setSelectedValue] = useState(value);
  const selected = useMemo(() => options?.find((opt) => opt.id === value?.id), [options, value?.id]);
  useClickOutside(wrapperRef, stopEditing);

  const handleChange = (newValue: any[]) => {
    /** We need to abandon resetting form if Add New was clicked */
    if (newValue[0] && !Object.hasOwn(newValue[0], 'deleted')) {
      return;
    }

    setSelectedValue(newValue[0]);

    if (tab === BulkImportEntityType.PRODUCTS) {
      const product = options?.find((opt) => opt.id === newValue[0]?.id) as Product;

      dispatch(bulkImportActions.updateInvalidProduct({
        id: data.id,
        entity: {
          createNew: data.entity.createNew,
          id: newValue[0]?.id || null,
          name: newValue[0]?.name || null,
        },
        revenueType: {
          createNew: data.revenueType.createNew,
          id: product ? product.revenueType : null,
        },
      }));
    }

    if (tab === BulkImportEntityType.CUSTOMERS) {
      dispatch(bulkImportActions.updateInvalidCustomer({
        id: data.id,
        entity: {
          createNew: data.entity.createNew,
          id: newValue[0]?.id || null,
          name: newValue[0]?.name || null,
        },
      }));
    }

    if (tab === BulkImportEntityType.TAGS) {
      dispatch(bulkImportActions.updateInvalidTag({
        id: data.id,
        entity: {
          createNew: data.entity.createNew,
          id: newValue[0]?.id || null,
          name: newValue[0]?.name || null,
        },
      }));
    }

    stopEditing();
  };

  const handleCreateNew = ({ name }: { name: string }) => {
    switch (tab) {
      case BulkImportEntityType.CUSTOMERS:
        dispatch(formStateActions.setPredefinedData({ name }));
        return setSelectedPane('addCustomer');
      case BulkImportEntityType.PRODUCTS:
        dispatch(formStateActions.setPredefinedData({ name }));
        return setSelectedPane('addProduct');
      case BulkImportEntityType.TAGS:
        dispatch(formStateActions.setPredefinedData({ name, type: 'Contract' }));
        return setSelectedPane('addTag');
    }
  };

  useImperativeHandle(ref, () => ({
    getValue() {
      return selectedValue;
    },
  }));

  if (disabled) return null;

  return (
    <DropdownRendererWrapper
      ref={wrapperRef}
      style={{ width: `${width}px` }}
    >
      <Dropdown
        ref={ref}
        values={selected ? [selected] : []}
        options={options || []}
        labelField="name"
        valueField="id"
        searchBy="name"
        onChange={handleChange}
        placeholder={value?.createNew ? '-' : 'Select...'}
        borderless
        disabled={disabled}
        maxHeight={maxHeight}
        create
        onCreateNew={handleCreateNew}
        keepOpen
      />
    </DropdownRendererWrapper>
  );
});
